import { classes } from 'html-classes';
import { sortBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import QRApp from '~/assets/img/qr_footer.png';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import {
  FooterSEOSection,
  SEO_ITEMS,
} from '~/components/isolated/FooterSEOSection';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import type { PaymentMethods } from '~/types/payments';

import { FooterSocialLinks } from '../FooterSocialLinks';
import { FooterSupportButton } from '../FooterSupportButton';

import styles from './FooterCD.module.scss';
import { FooterCDNavLinks } from './FooterCDNavLinks';

const METHODS: Record<PaymentMethods, { icon: IconTypes; width?: number }> = {
  cash: {
    icon: 'pay-cash-mono',
    width: 60,
  },
  gpay: {
    icon: 'pay-gpay-mono',
  },
  applepay: {
    icon: 'pay-apple-mono',
  },
  visa: {
    icon: 'pay-visa-mono',
  },
  mc: {
    icon: 'pay-mc-mono',
  },
  jcb: {
    icon: 'pay-jcb-mono',
  },
  unionpay: {
    icon: 'pay-unionpay-mono',
  },
  amex: {
    icon: 'pay-amex-mono',
  },
  discover: {
    icon: 'pay-discover-mono',
  },
};

const FooterCD = () => {
  useModal();
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();

  const isCategoryPage = /^\/c\/(.*?)$/gi.test(pathname);

  return (
    <>
      {!isMobile && !isCategoryPage && <FooterSEOSection />}

      <footer className={styles.footer} data-company={company.name}>
        <div className={styles.footer__container}>
          <section className={styles.footer__logoSection}>
            <Link to="/" className={styles.footer__logoSection__logo} />
            <div>
              {isMobile && (
                <FooterSupportButton className={styles.footer__supportButton} />
              )}
            </div>
          </section>

          <section className={styles.footer__downloadAppSection}>
            {isMobile ? (
              <>
                <a
                  href={company.config.appStoreUrl.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes([
                    styles.footer__downloadApp__button,
                    styles.footer__downloadApp__googlePlay,
                  ])}
                />
                <a
                  href={company.config.appStoreUrl.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes([
                    styles.footer__downloadApp__button,
                    styles.footer__downloadApp__appStore,
                  ])}
                />
              </>
            ) : (
              <div className={styles.footer__downloadApp__qrWr}>
                <img
                  src={QRApp}
                  alt=""
                  className={styles.footer__downloadApp__qr}
                />
                <p className={styles.footer__downloadApp__label}>
                  {t('downloadTheApp')}
                </p>
              </div>
            )}
          </section>

          <FooterCDNavLinks />

          <section className={styles.footer__adventagesSection}>
            <div className={styles.footer__innerTop}>
              {sortBy(SEO_ITEMS, 'order').map((item) => (
                <div className={styles.footer__innerTop__item} key={item.order}>
                  <p>
                    {item.icon && <Icon type={item.icon as IconTypes} />}
                    {item.title}
                  </p>
                  {isMobile && item.order === 0 ? (
                    <>
                      {item.caption && <span>{item.caption}</span>}
                      {item.captions && (
                        <div className={styles.footer_descriptionCaptions}>
                          {item.captions?.map((caption, i) => (
                            <p key={i}>{caption}</p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {item.caption && <span>{item.caption}</span>}
                      {item.captions && (
                        <div className={styles.footer_descriptionCaptions}>
                          {item.captions?.map((caption, i) => (
                            <p key={i}>
                              {caption.split('\n').map((line) => (
                                <span key={line}>{line}</span>
                              ))}
                            </p>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        {!!company.config.paymentMethods?.length && (
          <div className={styles.footer__container}>
            <div className={styles.footer__paymentMethods}>
              {company.config.paymentMethods.map((method) => {
                if (!METHODS[method]) {
                  return null;
                }

                const { icon, width } = METHODS[method];

                return (
                  <div key={method}>
                    <Icon
                      size={40}
                      height={40}
                      width={width}
                      type={icon}
                      className={styles.footer__paymentMethodIcon}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.footer__container}>
          <section className={styles.footer__bottom}>
            <div className={styles.footer__copyWr}>
              <div className={styles.footer_copyright}>
                <h3>&copy; {t('phrases:generalTrading')}</h3>
                <p>
                  {t('copyRightAddress:line1')} {t('copyRightAddress:line2')}
                </p>
              </div>
              <div className={styles.footer_license}>
                <p className={styles.licenseText}>
                  {t('phrases:licenseNumber')}{' '}
                  {t('phrases:taxRegistrationNumber')}
                </p>
              </div>
            </div>
            <FooterSocialLinks className={styles.footer_social}>
              {!isMobile && (
                <FooterSupportButton className={styles.footer__supportButton} />
              )}
            </FooterSocialLinks>
          </section>
        </div>
      </footer>
    </>
  );
};

export default FooterCD;
